import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { enqueueSnackbar } from "notistack";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import getUserRole from "../../helpers/jwt-token-access/userRole";
import {
  deleteScimToken,
  deleteSsoConfig,
  getScimConfig,
  postScimToken,
  postSsoConfig,
  postSsoConfigTest,
  putSsoConfig,
} from "../../helpers/auth_aws_helper";
import { getApiErrorMessage } from "../../helpers/aws_api_helper";
import { ACCOUNT_DETAIL_FONT_SX } from "../../components/AccountDetailPolicy/accountDetailTypography";
import {
  clearSsoConfigMessages,
  fetchSsoConfig,
  saveSsoConfig,
  saveSsoConfigFailure,
} from "../../store/ssoConfig/ssoConfigSlice";

const PROVIDERS = [
  { value: "OKTA", displayName: "Okta" },
  { value: "ONELOGIN", displayName: "OneLogin" },
  { value: "AD", displayName: "AD", label: "Microsoft Entra ID" },
];

// Only Okta and OneLogin can be selected; other values remain resolvable for existing configurations.
const PROVIDER_OPTIONS = PROVIDERS.filter(
  (provider) => provider.value === "OKTA" || provider.value === "ONELOGIN"
);

const DEFAULT_FIELD_LABELS = {
  acsUrl: "Single Sign-On URL",
  audienceUri: "Audience URI / SP Entity ID",
  metadataUrl: "SAML Metadata URL",
  relayState: "Default RelayState",
};

const ONELOGIN_FIELD_LABELS = {
  acsUrl: "ACS (Consumer) URL",
  audienceUri: "Audience (EntityID)",
  metadataUrl: "Issuer URL",
  relayState: "RelayState",
};

const PREFILL_DISPLAY_NAMES = PROVIDERS.map((provider) => provider.displayName);

function resolveProvider(providerType) {
  if (!providerType) return null;
  const normalized = String(providerType).trim().toUpperCase();
  return PROVIDERS.find((item) => item.value === normalized) || null;
}

// OneLogin's ACS URL Validator is the ACS URL as a full-match regex (escape regex metacharacters and `/`).
function acsUrlValidatorPattern(acsUrl) {
  const value = String(acsUrl || "").trim();
  if (!value) return "";
  return `^${value.replace(/[.*+?^${}()|[\]\\/]/g, "\\$&")}$`;
}

function getSsoGuideInformation({ phase, providerLabel, fieldLabels, isOneLogin }) {
  if (phase === "create") {
    return {
      title: "Set Up Single Sign-On",
      subtitle: `Follow these steps to create your ${providerLabel} configuration.`,
      steps: [
        "Select the Provider, then enter Display name and Email domain.",
        `Copy ${fieldLabels.acsUrl} and ${fieldLabels.audienceUri} into your ${providerLabel} SAML application.`,
        ...(isOneLogin
          ? [
              "Copy ACS (Consumer) URL Validator into OneLogin as the ACS URL Validator value.",
            ]
          : []),
        `In ${providerLabel}, finish the SAML application setup and copy its ${fieldLabels.metadataUrl}.`,
        `Paste the ${fieldLabels.metadataUrl} here, run Test Connection, then click Create Configuration.`,
      ],
    };
  }

  if (phase === "enable") {
    return {
      title: "Enable Single Sign-On",
      subtitle: `Complete ${providerLabel} setup, then turn Single Sign-On on.`,
      steps: [
        `Copy the SAML request-signing certificate into ${providerLabel}.`,
        `If you require encrypted SAML assertions, turn that setting on and save, then add the assertion-encryption certificate to ${providerLabel}.`,
        `Confirm the ${fieldLabels.metadataUrl} and run Test Connection if you changed it.`,
        "Turn on Enable Single Sign-On and click Update.",
        "After Single Sign-On is Active, configure SCIM provisioning if needed.",
      ],
    };
  }

  return {
    title: "Manage Single Sign-On",
    subtitle: `Your ${providerLabel} Single Sign-On configuration is Active.`,
    steps: [
      `Update Display name, Email domain, or ${fieldLabels.metadataUrl} as needed, then click Update.`,
      `To change encrypted assertions, update the setting here, then update ${providerLabel} to match before relying on the change.`,
      "Manage the SCIM token in SCIM Provisioning below.",
      "Use Delete only if you intend to remove this organization's Single Sign-On configuration.",
    ],
  };
}

const DOMAIN_PATTERN =
  /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i;

const EDITABLE_FIELDS = [
  "displayName",
  "targetEmailDomain",
  "idpMetadataUrl",
  "enabled",
  "configStatus",
  "requireEncryptedAssertions",
];

const TEST_REQUIRED_MESSAGE =
  "Run Test Connection on this metadata URL before turning on Single Sign-On.";
const TEST_FAILED_FALLBACK = "Test Connection failed. Check the metadata URL and try again.";

const SCIM_NOT_CONFIGURED = "SCIM token is not configured.";
const SCIM_SAVE_FIRST_MESSAGE = "Save the Single Sign-On configuration to set up SCIM provisioning.";
const SCIM_LOAD_FALLBACK = "SCIM settings could not be loaded. Try again.";
const SCIM_ACTION_FALLBACK = "The SCIM token could not be updated. Try again.";
const SCIM_TOKEN_FALLBACK_MESSAGE = "Copy this token now. It will not be shown again.";
const SETUP_REQUIRED_MESSAGE =
  "Save as Draft and add the required certificates to your identity provider before turning on Single Sign-On.";
const ENCRYPTION_CHANGE_MESSAGE =
  "Turn off Single Sign-On before changing the encrypted assertion requirement. Save that change, update your identity provider, then turn Single Sign-On back on.";
const DELETE_FALLBACK = "The Single Sign-On configuration could not be deleted. Try again.";

const BRAND_ORANGE = "#F98125";
const BRAND_ORANGE_HOVER = "#e8741c";

const FIELD_LABEL_SX = {
  display: "block",
  mb: 0.75,
  fontSize: "0.8125rem",
  fontWeight: 600,
  lineHeight: 1.4,
  color: "#334155",
};

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    fontSize: "0.875rem",
    backgroundColor: "#fff",
    borderRadius: "8px",
    color: "#111827",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&.Mui-focused fieldset": {
      borderColor: "#111827",
      borderWidth: "1px",
    },
    "&:hover:not(.Mui-disabled) fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-disabled": {
      backgroundColor: "#f3f4f6",
      "& fieldset": { borderColor: "#e5e7eb" },
    },
    "&.Mui-disabled .MuiOutlinedInput-input, &.Mui-disabled .MuiSelect-select": {
      WebkitTextFillColor: "#64748b",
    },
    "&.Mui-disabled .MuiSelect-icon": {
      color: "#94a3b8",
    },
  },
  "& .MuiOutlinedInput-notchedOutline legend": {
    float: "unset",
    width: "auto",
    maxWidth: "0.01px",
    margin: 0,
    padding: 0,
    fontSize: "0.75em",
    lineHeight: "11px",
    height: "11px",
  },
  "& .MuiOutlinedInput-input::placeholder": {
    color: "#94a3b8",
    opacity: 1,
  },
  // Keep Chrome autofill from painting inputs blue; match the field's normal white background.
  "& .MuiOutlinedInput-input:-webkit-autofill": {
    WebkitBoxShadow: "0 0 0 1000px #fff inset",
    WebkitTextFillColor: "#111827",
    caretColor: "#111827",
  },
  "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-input:-webkit-autofill": {
    WebkitBoxShadow: "0 0 0 1000px #f3f4f6 inset",
    WebkitTextFillColor: "#64748b",
    caretColor: "#64748b",
  },
};

const INPUT_ERROR_SX = {
  "& .MuiOutlinedInput-root.Mui-error fieldset, & .MuiOutlinedInput-root.Mui-error:hover fieldset, & .MuiOutlinedInput-root.Mui-error.Mui-focused fieldset":
    { borderColor: "#dc2626" },
  "& .MuiFormHelperText-root": {
    mx: 0,
    mt: 0.75,
    fontSize: "0.75rem",
    lineHeight: 1.4,
  },
  "& .MuiFormHelperText-root.Mui-error": { color: "#dc2626" },
};

// Chip reflects the last saved Create/Update response, not the local Enable toggle.
const STATUS_CHIP_SX = (active) => ({
  height: 24,
  fontSize: "0.75rem",
  fontWeight: 600,
  borderRadius: "6px",
  backgroundColor: active ? "#ecfdf5" : "#fff7ed",
  color: active ? "#047857" : BRAND_ORANGE,
  border: `1px solid ${active ? "#a7f3d0" : "#fdba74"}`,
  "& .MuiChip-label": { px: 1.25 },
});

const TOGGLE_ROW_SX = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 2,
  px: 2,
  py: 1.5,
  border: "1px solid #e2e8f0",
  borderRadius: "8px",
  backgroundColor: "#fff",
};

const ENABLED_SWITCH_SX = {
  flexShrink: 0,
  mr: -1,
  "& .MuiSwitch-switchBase.Mui-checked": { color: BRAND_ORANGE },
  "& .MuiSwitch-switchBase.Mui-checked:hover": {
    backgroundColor: "rgba(249, 129, 37, 0.08)",
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: BRAND_ORANGE,
    opacity: 0.5,
  },
  // A locked switch (e.g. encryption while SSO is Active) reads as unchangeable: neutral grey, even when on.
  "& .MuiSwitch-switchBase.Mui-checked.Mui-disabled": { color: "#94a3b8" },
  "& .MuiSwitch-switchBase.Mui-checked.Mui-disabled + .MuiSwitch-track": {
    backgroundColor: "#94a3b8",
    opacity: 0.45,
  },
};

const SELECT_MENU_PROPS = {
  PaperProps: {
    sx: {
      maxHeight: 320,
      borderRadius: "8px",
      border: "1px solid #e2e8f0",
      boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
      "& .MuiMenuItem-root": {
        fontSize: "0.875rem",
      },
      "& .MuiMenuItem-root.Mui-selected": {
        backgroundColor: "rgba(249, 129, 37, 0.12)",
      },
      "& .MuiMenuItem-root.Mui-selected:hover": {
        backgroundColor: "rgba(249, 129, 37, 0.18)",
      },
      "& .MuiMenuItem-root:hover": {
        backgroundColor: "#fff4eb",
      },
    },
  },
};

const TWO_COL_SX = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
  gap: 2,
  alignItems: "start",
};

const VALUE_BOX_SX = {
  minHeight: 40,
  display: "flex",
  alignItems: "center",
  minWidth: 0,
  border: "1px solid #e5e7eb",
  borderRadius: "8px",
  backgroundColor: "#f3f4f6",
  px: 1.5,
  py: 1,
  cursor: "default",
};

const REQUIRED_MARK_SX = {
  color: "#dc2626",
  ml: 0.25,
};

const PRIMARY_BTN_SX = {
  height: 40,
  minWidth: { xs: "100%", sm: 168 },
  width: { xs: "100%", sm: "auto" },
  maxWidth: "100%",
  backgroundColor: BRAND_ORANGE,
  color: "#fff",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.875rem",
  lineHeight: 1.25,
  borderRadius: "8px",
  px: { xs: 2, sm: 2.5 },
  whiteSpace: "nowrap",
  boxShadow: "none",
  "&:hover": { backgroundColor: BRAND_ORANGE_HOVER, boxShadow: "none" },
  "&.Mui-disabled": {
    backgroundColor: "#e5e7eb",
    color: "#9ca3af",
  },
};

const COPY_BTN_SX = {
  height: 40,
  minWidth: 92,
  flexShrink: 0,
  px: 1.75,
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.8125rem",
  borderRadius: "8px",
  border: "1px solid #e2e8f0",
  backgroundColor: "#fff",
  color: "#111827",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#f8fafc",
    borderColor: "#cbd5e1",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    color: "#9ca3af",
    borderColor: "#e5e7eb",
    backgroundColor: "#fff",
  },
};

const DELETE_BTN_SX = {
  ...COPY_BTN_SX,
  minWidth: 112,
  color: "#dc2626",
  borderColor: "#fca5a5",
  "&:hover": {
    backgroundColor: "#fef2f2",
    borderColor: "#dc2626",
    color: "#b91c1c",
    boxShadow: "none",
  },
};

const TEST_BTN_SX = {
  ...COPY_BTN_SX,
  minWidth: { xs: "100%", sm: 140 },
  width: { xs: "100%", sm: "auto" },
  whiteSpace: "nowrap",
  color: BRAND_ORANGE,
  borderColor: BRAND_ORANGE,
  "&:hover": {
    backgroundColor: "#fff7ed",
    borderColor: BRAND_ORANGE_HOVER,
    color: BRAND_ORANGE_HOVER,
    boxShadow: "none",
  },
};

const ALERT_SX = {
  borderRadius: "8px",
  fontSize: "0.8125rem",
  alignItems: "center",
};

const PROGRESS_SX = {
  borderRadius: "4px",
  backgroundColor: "#ffedd5",
  "& .MuiLinearProgress-bar": { backgroundColor: BRAND_ORANGE },
};

const HELP_TEXT_SX = {
  color: "#64748b",
  fontSize: "0.8125rem",
  lineHeight: 1.5,
};

const DIALOG_CANCEL_BTN_SX = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: 14,
  borderRadius: "8px",
  px: 2.5,
  color: "#475569",
  borderColor: "#cbd5e1",
  "&:hover": { borderColor: "#94a3b8", backgroundColor: "#f8fafc" },
};

const DIALOG_CONFIRM_BTN_SX = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: 14,
  borderRadius: "8px",
  px: 2.5,
  boxShadow: "none",
  backgroundColor: BRAND_ORANGE,
  "&:hover": { backgroundColor: BRAND_ORANGE_HOVER, boxShadow: "none" },
};

const EMPTY_FORM = {
  providerType: "OKTA",
  displayName: "Okta",
  targetEmailDomain: "",
  idpMetadataUrl: "",
  enabled: false,
  configStatus: "DRAFT",
  requireEncryptedAssertions: false,
  protocol: "SAML",
  acsUrl: "",
  audienceUri: "",
  cognitoProviderName: "",
};

function readRole() {
  try {
    return getUserRole();
  } catch (error) {
    return "NONE";
  }
}

function isHttpsUrl(value) {
  if (/\s/.test(value)) return false;
  try {
    const parsed = new URL(value);
    return parsed.protocol === "https:" && Boolean(parsed.hostname);
  } catch (error) {
    return false;
  }
}

function formatDate(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function comparableValue(form, field) {
  const value = form[field];
  return typeof value === "string" ? value.trim() : value;
}

function isSavedConfig(config) {
  return Boolean(config && String(config.idpMetadataUrl || "").trim());
}

function domainFromEmail(email) {
  if (!email || typeof email !== "string") return "";
  const at = email.lastIndexOf("@");
  if (at < 1 || at === email.length - 1) return "";
  return email.slice(at + 1).toLowerCase();
}

function readOrgEmailDomain(organization) {
  return domainFromEmail(
    organization?.orgprimaryemail || organization?.orgbillingemail
  );
}

function formFromConfig(config, fallbackProvider) {
  const provider =
    resolveProvider(config?.providerType) ||
    resolveProvider(fallbackProvider) ||
    resolveProvider("OKTA");
  return {
    providerType: provider.value,
    displayName: config?.displayName || provider.displayName || "",
    targetEmailDomain: config?.targetEmailDomain || "",
    idpMetadataUrl: config?.idpMetadataUrl || "",
    enabled: Boolean(config?.enabled),
    configStatus: config?.status || "DRAFT",
    requireEncryptedAssertions: Boolean(config?.requireEncryptedAssertions),
    protocol: config?.protocol || "SAML",
    acsUrl: config?.acsUrl || "",
    audienceUri: config?.audienceUri || "",
    cognitoProviderName: config?.cognitoProviderName || "",
  };
}

export default function SsoConfig() {
  const dispatch = useDispatch();
  const { loading, saving, config, error, saveError, saveMessage } = useSelector(
    (state) => state.ssoConfig
  );
  // Configuration loads `/cloudnova/accounts` into acctList; orgAccount is a second path for the same API.
  const organization = useSelector((state) => {
    const fromAcctList = state.acctList?.organizations;
    const fromOrgAccount = state.orgAccount?.organizations;
    const list =
      Array.isArray(fromAcctList) && fromAcctList.length > 0
        ? fromAcctList
        : Array.isArray(fromOrgAccount) && fromOrgAccount.length > 0
          ? fromOrgAccount
          : null;
    return list ? list[0] : null;
  });
  const role = readRole();
  const canWrite = role === "Administrator";
  const [form, setForm] = useState(EMPTY_FORM);
  const [hasExistingConfig, setHasExistingConfig] = useState(false);
  const [copiedField, setCopiedField] = useState("");
  const [touched, setTouched] = useState({});
  const [confirmTurnOffOpen, setConfirmTurnOffOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sequentialSaving, setSequentialSaving] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [sidePanelOpen, setSidePanelOpen] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(min-width: 900px)").matches : true
  );
  const savedForm = isSavedConfig(config) ? formFromConfig(config) : null;
  const lockedProvider = isSavedConfig(config)
    ? resolveProvider(config.providerType)?.value || ""
    : "";
  const lockedProviderLabel =
    resolveProvider(lockedProvider)?.label ||
    resolveProvider(lockedProvider)?.displayName ||
    lockedProvider;
  const providerLabel =
    resolveProvider(form.providerType)?.label ||
    resolveProvider(form.providerType)?.displayName ||
    form.providerType;
  const isOneLogin = resolveProvider(form.providerType)?.value === "ONELOGIN";
  const fieldLabels = isOneLogin ? ONELOGIN_FIELD_LABELS : DEFAULT_FIELD_LABELS;
  const relayState = config?.idpInitiatedRelayState || "";
  const acsUrlValidator = isOneLogin ? acsUrlValidatorPattern(form.acsUrl) : "";
  const signingCertificate = config?.requestSigningCertificate || "";
  const encryptionCertificate = config?.assertionEncryptionCertificate || "";
  const supportsCertificates = Boolean(
    config &&
      typeof config === "object" &&
      ("requestSigningCertificate" in config || "assertionEncryptionCertificate" in config)
  );
  // The signing certificate is always relevant; the encryption certificate only when encryption is on.
  const certificatesReady =
    Boolean(signingCertificate) &&
    (!form.requireEncryptedAssertions || Boolean(encryptionCertificate));
  // The encryption certificate is tied to its toggle: shown below it only while it is on.
  const showEncryptionCertificate = Boolean(form.requireEncryptedAssertions);
  const encryptionChanged =
    Boolean(form.requireEncryptedAssertions) !==
    Boolean(savedForm?.requireEncryptedAssertions);
  // The toggles are independent: neither locks the other. A combined encryption + enable save is sequenced on submit (below), because the backend rejects changing encryption in a request that also enables SSO.
  const encryptionSwitchDisabled = !canWrite;
  const isEncryptionChangeError =
    saveError?.status === 400 &&
    typeof saveError?.message === "string" &&
    saveError.message.toLowerCase().includes("encrypted assertion");
  const saveErrorMessage = isEncryptionChangeError
    ? ENCRYPTION_CHANGE_MESSAGE
    : saveError?.message;
  const configSaved = isSavedConfig(config);
  // Before the first save, the card follows the setup order; saved configurations keep the full layout.
  const isInitialState = !configSaved;
  const orgEmailDomain = readOrgEmailDomain(organization);
  const metadataUrl = form.idpMetadataUrl.trim();

  // Backends without Test Connection omit the test fields; enforcing the test there would block enabling SSO.
  const supportsTest = Boolean(
    config && typeof config === "object" && "lastTestStatus" in config
  );
  const currentTest =
    testResult &&
    testResult.url === metadataUrl &&
    testResult.providerType === form.providerType
      ? testResult
      : null;
  const savedPassedTest =
    config?.lastTestStatus === "PASSED" &&
    String(config?.lastTestedMetadataUrl || "").trim() === metadataUrl;
  const liveUrlUnchanged = Boolean(
    savedForm?.enabled && savedForm.idpMetadataUrl.trim() === metadataUrl
  );
  const testRequired =
    supportsTest && !liveUrlUnchanged && !savedPassedTest && !currentTest?.success;

  // Turning on requires a passing test and, once certificates are returned, a saved config with the required certificates.
  const setupRequired =
    supportsCertificates && !savedForm?.enabled && (!configSaved || !certificatesReady);
  const enabledSwitchDisabled = !canWrite;
  // Create/Update success writes the response into `config`; the chip follows that, not the toggle.
  const ssoActive = Boolean(savedForm?.enabled);
  const guidePhase = !configSaved ? "create" : ssoActive ? "manage" : "enable";
  const guideInfo = getSsoGuideInformation({
    phase: guidePhase,
    providerLabel,
    fieldLabels,
    isOneLogin,
  });

  useEffect(() => {
    dispatch(fetchSsoConfig({ providerType: "OKTA" }));
    return () => {
      dispatch(clearSsoConfigMessages());
    };
  }, [dispatch]);

  // Keep the guide collapsed on narrow viewports so the SSO form stays usable.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(max-width: 899px)");
    const syncGuideForViewport = () => {
      if (mq.matches) setSidePanelOpen(false);
    };
    syncGuideForViewport();
    if (mq.addEventListener) {
      mq.addEventListener("change", syncGuideForViewport);
      return () => mq.removeEventListener("change", syncGuideForViewport);
    }
    mq.addListener(syncGuideForViewport);
    return () => mq.removeListener(syncGuideForViewport);
  }, []);

  useEffect(() => {
    if (!config) return;
    if (isSavedConfig(config)) {
      setForm(formFromConfig(config));
      setHasExistingConfig(true);
      return;
    }
    setForm((current) => {
      const next = formFromConfig(config, current.providerType);
      const keepDisplayName =
        current.displayName &&
        !PREFILL_DISPLAY_NAMES.includes(current.displayName);
      const domain =
        current.targetEmailDomain.trim() ||
        readOrgEmailDomain(organization) ||
        next.targetEmailDomain;
      return {
        ...current,
        providerType: next.providerType || current.providerType,
        displayName: keepDisplayName ? current.displayName : next.displayName,
        targetEmailDomain: domain,
        requireEncryptedAssertions: next.requireEncryptedAssertions,
        protocol: next.protocol || current.protocol,
        acsUrl: next.acsUrl,
        audienceUri: next.audienceUri,
        cognitoProviderName: next.cognitoProviderName,
      };
    });
  }, [config, organization]);

  useEffect(() => {
    if (!orgEmailDomain) return;
    setForm((current) => {
      if (current.targetEmailDomain.trim()) return current;
      return { ...current, targetEmailDomain: orgEmailDomain };
    });
  }, [orgEmailDomain]);

  useEffect(() => {
    if (saveError?.status === 409) setHasExistingConfig(true);
    if (saveError?.status === 404) setHasExistingConfig(false);
  }, [saveError]);

  useEffect(() => {
    if (!saveMessage) return;
    // Reload after Create/Update so generated certificates are shown.
    dispatch(
      fetchSsoConfig({
        providerType: resolveProvider(config?.providerType)?.value || form.providerType,
      })
    );
    dispatch(clearSsoConfigMessages());
  }, [saveMessage, dispatch, config?.providerType, form.providerType]);

  useEffect(() => {
    if (!copiedField) return undefined;
    const timer = setTimeout(() => setCopiedField(""), 2000);
    return () => clearTimeout(timer);
  }, [copiedField]);

  const setField = (field) => (event) => {
    const value =
      event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((current) => {
      if (field !== "enabled") return { ...current, [field]: value };
      return {
        ...current,
        enabled: value,
        configStatus: value ? "ACTIVE" : hasExistingConfig ? "DISABLED" : "DRAFT",
      };
    });
  };

  const markTouched = (field) => () => {
    setTouched((current) => (current[field] ? current : { ...current, [field]: true }));
  };

  const handleProviderChange = (event) => {
    if (lockedProvider) return;
    const provider = resolveProvider(event.target.value);
    if (!provider) return;
    setForm((current) => ({
      ...current,
      providerType: provider.value,
      displayName: PREFILL_DISPLAY_NAMES.includes(current.displayName)
        ? provider.displayName
        : current.displayName,
    }));
    setCopiedField("");
    dispatch(clearSsoConfigMessages());
    dispatch(fetchSsoConfig({ providerType: provider.value }));
  };

  const copyValue = async (field, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
    } catch (copyError) {
      setCopiedField("");
    }
  };

  const downloadCertificate = (filename, value) => {
    if (!value) return;
    // The backend returns valid PEM; download it exactly as returned.
    const blob = new Blob([value], {
      type: "text/plain;charset=utf-8",
    });
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(downloadUrl);
  };

  const handleDeleteSsoConfig = async () => {
    setDeleting(true);
    setDeleteError("");
    try {
      const response = await deleteSsoConfig();
      // 204 has no body; do not read response.data.
      if (response?.status !== 204) {
        setDeleteError(getApiErrorMessage(response));
        return;
      }
      const providerType = lockedProvider || form.providerType;
      setConfirmDeleteOpen(false);
      setHasExistingConfig(false);
      setTestResult(null);
      setTouched({});
      setCopiedField("");
      setForm((current) => ({
        ...EMPTY_FORM,
        providerType: current.providerType,
        displayName: resolveProvider(current.providerType)?.displayName || "Okta",
        targetEmailDomain: orgEmailDomain || "",
      }));
      dispatch(fetchSsoConfig({ providerType }));
    } catch (deleteErr) {
      setDeleteError(DELETE_FALLBACK);
    } finally {
      setDeleting(false);
    }
  };

  const handleTestConnection = async () => {
    const url = metadataUrl;
    const providerType = form.providerType;
    setTesting(true);
    setTestResult(null);
    try {
      const response = await postSsoConfigTest({
        providerType,
        protocol: "SAML",
        idpMetadataUrl: url,
      });
      const data = response?.data;
      const success = response?.status === 200 && data?.success === true;
      const message =
        (typeof data?.message === "string" && data.message.trim()) ||
        (!success ? getApiErrorMessage(response) : "") ||
        (success ? "SAML metadata validated successfully." : TEST_FAILED_FALLBACK);
      setTestResult({
        url,
        providerType,
        success,
        message,
        certificateExpiry: success ? formatDate(data?.metadata?.certificateExpiry) : "",
      });
    } catch (testError) {
      setTestResult({
        url,
        providerType,
        success: false,
        message:
          getApiErrorMessage(testError?.response || testError) || TEST_FAILED_FALLBACK,
      });
    } finally {
      setTesting(false);
    }
  };

  const submitSave = async () => {
    setConfirmTurnOffOpen(false);
    dispatch(clearSsoConfigMessages());
    const body = {
      enabled: form.enabled,
      providerType: form.providerType,
      displayName: form.displayName.trim(),
      protocol: "SAML",
      targetEmailDomain: form.targetEmailDomain.trim(),
      idpMetadataUrl: form.idpMetadataUrl.trim(),
      requireEncryptedAssertions: Boolean(form.requireEncryptedAssertions),
      status: form.enabled ? "ACTIVE" : hasExistingConfig ? "DISABLED" : "DRAFT",
    };

    // The backend rejects changing the encryption requirement in a request that also
    // enables SSO, so a combined change is saved in two steps behind one click:
    // first with SSO off (which prepares/updates the encryption setting), then enabled.
    if (encryptionChanged && form.enabled) {
      setSequentialSaving(true);
      try {
        const firstBody = {
          ...body,
          enabled: false,
          status: hasExistingConfig ? "DISABLED" : "DRAFT",
        };
        const firstResponse = hasExistingConfig
          ? await putSsoConfig(firstBody)
          : await postSsoConfig(firstBody);
        if (firstResponse?.status !== 200 && firstResponse?.status !== 201) {
          dispatch(
            saveSsoConfigFailure({
              status: firstResponse?.status,
              message: getApiErrorMessage(firstResponse),
            })
          );
          return;
        }
        dispatch(saveSsoConfig({ method: "PUT", body }));
      } catch (sequenceError) {
        dispatch(
          saveSsoConfigFailure({
            status: sequenceError?.response?.status,
            message: getApiErrorMessage(sequenceError?.response || sequenceError),
          })
        );
      } finally {
        setSequentialSaving(false);
      }
      return;
    }

    dispatch(
      saveSsoConfig({
        method: hasExistingConfig ? "PUT" : "POST",
        body,
      })
    );
  };

  const isLiveSaved = Boolean(savedForm?.enabled);
  const willBeLive = form.enabled;

  const handleSave = () => {
    if (isLiveSaved && !willBeLive) {
      setConfirmTurnOffOpen(true);
      return;
    }
    submitSave();
  };

  const isDirty =
    !savedForm ||
    EDITABLE_FIELDS.some(
      (field) => comparableValue(form, field) !== comparableValue(savedForm, field)
    );

  const displayNameError = /\s/.test(form.displayName)
    ? "Display name cannot contain spaces."
    : "";

  const emailDomain = form.targetEmailDomain.trim();
  const emailDomainError =
    emailDomain && !DOMAIN_PATTERN.test(emailDomain)
      ? "Enter a domain like company.com, without @ or spaces."
      : "";

  const metadataUrlError =
    metadataUrl && !isHttpsUrl(metadataUrl)
      ? "Enter a valid URL that starts with https://."
      : "";

  const saveBlockedReason = () => {
    if (!form.displayName.trim()) return "Display name is required.";
    if (displayNameError) return displayNameError;
    if (!emailDomain) return "Email domain is required.";
    if (emailDomainError) return emailDomainError;
    if (!metadataUrl) return `${fieldLabels.metadataUrl} is required.`;
    if (metadataUrlError) return metadataUrlError;
    if (form.enabled && setupRequired) return SETUP_REQUIRED_MESSAGE;
    if (form.enabled && testRequired) return TEST_REQUIRED_MESSAGE;
    return "";
  };

  const blockedReason = saveBlockedReason();

  const showAlerts = Boolean(error || saveErrorMessage || !canWrite);

  const providerField = (
    <Field label="Provider" htmlFor="sso-provider">
      <Tooltip
        title={
          lockedProvider
            ? `This organization already uses ${lockedProviderLabel}. Only one identity provider can be configured.`
            : ""
        }
        placement="top"
        arrow
      >
        <Box component="span" sx={{ display: "block" }}>
          <TextField
            select
            fullWidth
            size="small"
            id="sso-provider"
            value={form.providerType}
            onChange={handleProviderChange}
            disabled={loading || Boolean(lockedProvider)}
            SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
            sx={INPUT_SX}
          >
            {PROVIDER_OPTIONS.map((provider) => (
              <MenuItem key={provider.value} value={provider.value}>
                {provider.label || provider.displayName}
              </MenuItem>
            ))}
            {lockedProvider &&
              !PROVIDER_OPTIONS.some((provider) => provider.value === lockedProvider) && (
                <MenuItem value={lockedProvider}>{lockedProviderLabel}</MenuItem>
              )}
          </TextField>
        </Box>
      </Tooltip>
    </Field>
  );

  const displayNameField = (
    <Field label="Display name" htmlFor="sso-display-name" required>
      <TextField
        fullWidth
        size="small"
        id="sso-display-name"
        value={form.displayName}
        onChange={setField("displayName")}
        autoComplete="off"
        disabled={!canWrite}
        required
        error={Boolean(displayNameError)}
        helperText={displayNameError || undefined}
        sx={{ ...INPUT_SX, ...INPUT_ERROR_SX }}
      />
    </Field>
  );

  const emailDomainField = (
    <Field label="Email domain" htmlFor="sso-email-domain" required>
      <TextField
        fullWidth
        size="small"
        id="sso-email-domain"
        value={form.targetEmailDomain}
        onChange={setField("targetEmailDomain")}
        autoComplete="off"
        onBlur={markTouched("targetEmailDomain")}
        placeholder="customer.com"
        disabled={!canWrite}
        required
        error={Boolean(touched.targetEmailDomain && emailDomainError)}
        helperText={(touched.targetEmailDomain && emailDomainError) || undefined}
        sx={{ ...INPUT_SX, ...INPUT_ERROR_SX }}
      />
    </Field>
  );

  const metadataUrlField = (
    <Field label={fieldLabels.metadataUrl} htmlFor="sso-metadata-url" required>
      <TextField
        fullWidth
        size="small"
        id="sso-metadata-url"
        value={form.idpMetadataUrl}
        onChange={setField("idpMetadataUrl")}
        autoComplete="off"
        onBlur={markTouched("idpMetadataUrl")}
        disabled={!canWrite}
        required
        error={Boolean(touched.idpMetadataUrl && metadataUrlError)}
        helperText={(touched.idpMetadataUrl && metadataUrlError) || undefined}
        sx={{ ...INPUT_SX, ...INPUT_ERROR_SX }}
        inputProps={{
          spellCheck: "false",
          title: form.idpMetadataUrl || undefined,
        }}
      />
    </Field>
  );

  const testConnectionControl =
    canWrite && supportsTest ? (
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="div"
          aria-hidden="true"
          sx={{ ...FIELD_LABEL_SX, visibility: "hidden", userSelect: "none" }}
        >
          Test
        </Typography>
        <Button
          onClick={handleTestConnection}
          disabled={testing || loading || !metadataUrl || Boolean(metadataUrlError)}
          sx={TEST_BTN_SX}
        >
          {testing ? "Testing..." : "Test Connection"}
        </Button>
      </Box>
    ) : null;

  const testConnectionStatus = canWrite && supportsTest ? (
    <TestConnectionStatus
      lastPassedOn={
        !currentTest && savedPassedTest ? formatDate(config?.lastTestedAt) : ""
      }
      testSuccess={currentTest ? currentTest.success : null}
      testMessage={currentTest?.message || ""}
    />
  ) : null;

  const singleSignOnUrlField = (
    <CopyField
      label={fieldLabels.acsUrl}
      value={form.acsUrl}
      copied={copiedField === "acsUrl"}
      onCopy={() => copyValue("acsUrl", form.acsUrl)}
    />
  );

  const acsUrlValidatorField = (
    <CopyField
      label="ACS (Consumer) URL Validator"
      value={acsUrlValidator}
      copied={copiedField === "acsUrlValidator"}
      onCopy={() => copyValue("acsUrlValidator", acsUrlValidator)}
    />
  );

  const audienceUriField = (
    <CopyField
      label={fieldLabels.audienceUri}
      value={form.audienceUri}
      copied={copiedField === "audienceUri"}
      onCopy={() => copyValue("audienceUri", form.audienceUri)}
    />
  );

  const relayStateField = (
    <CopyField
      label={fieldLabels.relayState}
      value={relayState}
      copied={copiedField === "relayState"}
      onCopy={() => copyValue("relayState", relayState)}
    />
  );

  const protocolField = <ReadOnlyValue label="Protocol" value={form.protocol} />;

  const signingCertificateField = (
    <CertificateField
      label="SAML request-signing certificate"
      description="Install this in your identity provider to validate Cognito-signed SAML requests."
      value={signingCertificate}
      copied={copiedField === "requestSigningCertificate"}
      onCopy={() => copyValue("requestSigningCertificate", signingCertificate)}
      onDownload={() =>
        downloadCertificate("request-signing-certificate.pem", signingCertificate)
      }
    />
  );

  const encryptionCertificateField = (
    <CertificateField
      label="SAML assertion-encryption certificate"
      hideLabel
      description="Add this certificate to your identity provider to encrypt SAML assertions sent to Cognito."
      value={encryptionCertificate}
      copied={copiedField === "assertionEncryptionCertificate"}
      onCopy={() =>
        copyValue("assertionEncryptionCertificate", encryptionCertificate)
      }
      onDownload={() =>
        downloadCertificate(
          "assertion-encryption-certificate.pem",
          encryptionCertificate
        )
      }
    />
  );

  const ssoEnabledToggle = (
    <Box sx={TOGGLE_ROW_SX}>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="label"
          htmlFor="sso-enabled"
          sx={{
            display: "block",
            fontSize: "0.875rem",
            fontWeight: 600,
            lineHeight: 1.4,
            color: enabledSwitchDisabled ? "#94a3b8" : "#111827",
            cursor: enabledSwitchDisabled ? "default" : "pointer",
          }}
        >
          Enable Single Sign-On
        </Typography>
        <Typography
          id="sso-enabled-description"
          sx={{ fontSize: "0.8125rem", lineHeight: 1.5, color: "#64748b", mt: 0.25 }}
        >
          Users set to Single Sign-On sign in through {providerLabel} instead of a password.
          {!form.enabled &&
            setupRequired &&
            " Save as Draft and add the required certificates to your identity provider first."}
          {!form.enabled &&
            testRequired &&
            " Run Test Connection on this metadata URL to turn this on."}
        </Typography>
      </Box>
      <Switch
        id="sso-enabled"
        checked={form.enabled}
        onChange={setField("enabled")}
        disabled={enabledSwitchDisabled}
        inputProps={{ "aria-describedby": "sso-enabled-description" }}
        sx={ENABLED_SWITCH_SX}
      />
    </Box>
  );

  const encryptionDisabledReason = !canWrite
    ? "Only an Administrator can change this setting."
    : "";

  const encryptionSection = (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        border: "1px solid #e2e8f0",
        borderRadius: "8px",
        backgroundColor: "#fff",
        display: "flex",
        flexDirection: "column",
        gap: 0,
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="label"
            htmlFor="sso-require-encrypted-assertions"
            sx={{
              display: "block",
              fontSize: "0.875rem",
              fontWeight: 600,
              lineHeight: 1.4,
              color: encryptionSwitchDisabled ? "#94a3b8" : "#111827",
              cursor: encryptionSwitchDisabled ? "default" : "pointer",
            }}
          >
            Require encrypted SAML assertions
          </Typography>
        </Box>
        <Tooltip title={encryptionDisabledReason} placement="top" arrow>
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              flexShrink: 0,
              cursor: encryptionSwitchDisabled ? "not-allowed" : "pointer",
            }}
          >
            <Switch
              id="sso-require-encrypted-assertions"
              checked={Boolean(form.requireEncryptedAssertions)}
              onChange={setField("requireEncryptedAssertions")}
              disabled={encryptionSwitchDisabled}
              sx={ENABLED_SWITCH_SX}
            />
          </Box>
        </Tooltip>
      </Box>
      {showEncryptionCertificate && encryptionCertificateField}
    </Box>
  );

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        minHeight: 0,
        flex: { md: 1 },
        height: { xs: "auto", md: "100%" },
        maxHeight: { xs: "none", md: "100%" },
        display: "flex",
        flexDirection: "column",
        overflow: { md: "hidden" },
        boxSizing: "border-box",
        ...ACCOUNT_DETAIL_FONT_SX,
      }}
    >
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: "stretch",
        gap: { xs: 1.5, md: 2 },
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        minHeight: 0,
        flex: { md: 1 },
        height: { md: "100%" },
        maxHeight: { md: "100%" },
        overflow: { md: "hidden" },
        boxSizing: "border-box",
      }}
    >
    <Box
      sx={{
        flex: "1 1 auto",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        minHeight: 0,
        height: { md: "100%" },
        maxHeight: { md: "100%" },
        display: "flex",
        flexDirection: "column",
        gap: 2,
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        p: { xs: 1.5, sm: 2, md: 2.5 },
        backgroundColor: "#f3f4f6",
        boxSizing: "border-box",
        // Single scroll on the outer SSO Configuration card only.
        overflowX: "hidden",
        overflowY: { xs: "visible", md: "auto" },
        overscrollBehavior: "contain",
        scrollbarWidth: "thin",
        scrollbarColor: "#94a3b8 #e5e7eb",
        "&::-webkit-scrollbar": { width: 8 },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "#e5e7eb",
          borderRadius: 8,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#94a3b8",
          borderRadius: 8,
          border: "2px solid #e5e7eb",
          backgroundClip: "padding-box",
        },
        "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#64748b" },
      }}
    >
      <Box
        sx={{
          pb: 1.25,
          borderBottom: "1px solid #e5e7eb",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: { xs: 1, sm: 2 },
          minWidth: 0,
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            color: "#111827",
            fontSize: { xs: "0.9375rem", sm: "1rem" },
            lineHeight: 1.35,
            minWidth: 0,
            flex: "1 1 auto",
          }}
        >
          Single Sign-On Configuration
        </Typography>
        <Chip
          label={ssoActive ? "Active" : "Inactive"}
          size="small"
          sx={{ ...STATUS_CHIP_SX(ssoActive), flexShrink: 0 }}
        />
      </Box>

      {(loading || saving) && <LinearProgress sx={{ ...PROGRESS_SX, flexShrink: 0 }} />}

      {showAlerts && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, flexShrink: 0 }}>
          {error && <Alert severity="error" sx={ALERT_SX}>{error}</Alert>}
          {saveErrorMessage && (
            <Alert severity="error" sx={ALERT_SX}>{saveErrorMessage}</Alert>
          )}
          {!canWrite && (
            <Alert severity="info" sx={ALERT_SX}>
              Only an Administrator can save Single Sign-On configuration.
            </Alert>
          )}
        </Box>
      )}

        <SectionCard>
          {isInitialState ? (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    md: sidePanelOpen ? "repeat(2, minmax(0, 1fr))" : "repeat(3, minmax(0, 1fr))",
                    lg: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 2,
                  alignItems: "start",
                  minWidth: 0,
                }}
              >
                {providerField}
                {displayNameField}
                {emailDomainField}
              </Box>
              {singleSignOnUrlField}
              {isOneLogin ? acsUrlValidatorField : null}
              {audienceUriField}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md:
                        canWrite && supportsTest
                          ? "minmax(120px, 160px) minmax(0, 1fr) auto"
                          : "minmax(120px, 160px) minmax(0, 1fr)",
                    },
                    gap: 2,
                    alignItems: "start",
                    minWidth: 0,
                  }}
                >
                  {protocolField}
                  {metadataUrlField}
                  {testConnectionControl}
                </Box>
              </Box>
            </>
          ) : (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    md: sidePanelOpen ? "repeat(2, minmax(0, 1fr))" : "repeat(3, minmax(0, 1fr))",
                    lg: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 2,
                  alignItems: "start",
                  minWidth: 0,
                }}
              >
                {providerField}
                {displayNameField}
                {emailDomainField}
              </Box>

              {singleSignOnUrlField}
              {isOneLogin ? acsUrlValidatorField : null}

              {audienceUriField}

              {relayState ? relayStateField : null}

              {supportsCertificates && signingCertificateField}

              {encryptionSection}
              {ssoEnabledToggle}

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md:
                      canWrite && supportsTest
                        ? "minmax(120px, 160px) minmax(0, 1fr) auto"
                        : "minmax(120px, 160px) minmax(0, 1fr)",
                  },
                  gap: 2,
                  alignItems: "start",
                  minWidth: 0,
                }}
              >
                {protocolField}
                {metadataUrlField}
                {testConnectionControl}
              </Box>
            </>
          )}

          {canWrite && (
            <Box sx={{ width: "100%", minWidth: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "stretch", sm: "center" },
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1.25,
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: { xs: "stretch", sm: "flex-start" },
                    order: { xs: 3, sm: 1 },
                    minWidth: 0,
                    flex: { sm: "0 0 auto" },
                  }}
                >
                  {configSaved && (
                    <Button
                      onClick={() => {
                        setDeleteError("");
                        setConfirmDeleteOpen(true);
                      }}
                      disabled={deleting || saving || sequentialSaving || loading}
                      sx={{ ...DELETE_BTN_SX, width: { xs: "100%", sm: "auto" } }}
                    >
                      {deleting ? "Deleting..." : "Delete"}
                    </Button>
                  )}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    gap: 0.5,
                    minWidth: 0,
                    flex: { sm: "1 1 160px" },
                    order: { xs: 1, sm: 2 },
                    px: { sm: 1 },
                  }}
                >
                  {testConnectionStatus}
                  {blockedReason && (
                    <Typography
                      sx={{
                        color: "#64748b",
                        fontSize: "0.8125rem",
                        lineHeight: 1.5,
                        textAlign: "center",
                      }}
                    >
                      {blockedReason}
                    </Typography>
                  )}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: { xs: "stretch", sm: "flex-end" },
                    minWidth: 0,
                    flex: { sm: "0 0 auto" },
                    order: { xs: 2, sm: 3 },
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  <Button
                    onClick={handleSave}
                    disabled={
                      saving || sequentialSaving || loading || Boolean(blockedReason) || !isDirty
                    }
                    sx={PRIMARY_BTN_SX}
                  >
                    {saving || sequentialSaving
                      ? "Saving..."
                      : hasExistingConfig
                        ? "Update"
                        : "Create Configuration"}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}

        </SectionCard>

        {config &&
          (configSaved ? (
            <ScimProvisioning canWrite={canWrite} />
          ) : (
            <SectionCard title="SCIM Provisioning">
              <Typography sx={HELP_TEXT_SX}>{SCIM_SAVE_FIRST_MESSAGE}</Typography>
            </SectionCard>
          ))}
    </Box>

    <SsoGuideSidePanel
      open={sidePanelOpen}
      onOpen={() => setSidePanelOpen(true)}
      onClose={() => setSidePanelOpen(false)}
      guideInfo={guideInfo}
    />
    </Box>

      <ConfirmTurnOffDialog
        open={confirmTurnOffOpen}
        onCancel={() => setConfirmTurnOffOpen(false)}
        onConfirm={submitSave}
      />
      <ConfirmDialog
        open={confirmDeleteOpen}
        title="Delete SSO Configuration"
        message="Are you sure you want to delete the Single Sign-On configuration for this organization?"
        detail="This also revokes the existing SCIM token. Users, roles, resource groups and account assignments are not deleted."
        confirmLabel={deleting ? "Deleting..." : "Delete"}
        confirmDisabled={deleting}
        confirmButtonSx={{
          ...DIALOG_CONFIRM_BTN_SX,
          backgroundColor: "#dc2626",
          "&:hover": { backgroundColor: "#b91c1c", boxShadow: "none" },
        }}
        error={deleteError}
        onCancel={() => {
          if (!deleting) setConfirmDeleteOpen(false);
        }}
        onConfirm={handleDeleteSsoConfig}
      />
    </Box>
  );
}

function isGuideMainStepText(step) {
  const isSubStep = step.startsWith("  ");
  const stepText = isSubStep ? step.trim() : step;
  return (
    !isSubStep &&
    !stepText.match(/^[a-z]\./) &&
    !stepText.match(/^[A-Z]\./) &&
    !stepText.match(/^[ivx]+\./) &&
    !stepText.match(/^[IVX]+\./)
  );
}

function SsoGuideSidePanel({ open, onOpen, onClose, guideInfo }) {
  if (!guideInfo?.steps?.length) return null;

  if (!open) {
    return (
      <Tooltip title="Show guide" placement="left" arrow>
        <Box
          component="button"
          type="button"
          onClick={onOpen}
          aria-label="Show Single Sign-On setup guide"
          sx={{
            display: "flex",
            flexDirection: { xs: "row", md: "column" },
            alignItems: "center",
            justifyContent: "center",
            gap: 0.75,
            flex: { xs: "0 0 auto", md: "0 0 40px" },
            alignSelf: "stretch",
            width: { xs: "100%", md: 40 },
            minHeight: { xs: 44, md: 0 },
            height: { md: "100%" },
            px: { xs: 1.5, md: 0.5 },
            py: { xs: 1, md: 1.5 },
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            backgroundColor: "#fff",
            boxShadow: "none",
            cursor: "pointer",
            color: BRAND_ORANGE,
            transition: "background-color 0.2s ease",
            "&:hover": {
              backgroundColor: "#fff7ed",
            },
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: 22, transform: { xs: "rotate(-90deg)", md: "none" } }} />
          <Typography
            sx={{
              writingMode: { xs: "horizontal-tb", md: "vertical-rl" },
              transform: { xs: "none", md: "rotate(180deg)" },
              fontSize: "0.75rem",
              fontWeight: 600,
              letterSpacing: "0.06em",
              color: "#334155",
              userSelect: "none",
            }}
          >
            Guide
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  let mainStepCounter = 0;

  return (
    <Box
      sx={{
        flex: {
          xs: "0 0 auto",
          md: "0 0 clamp(260px, 28%, 340px)",
        },
        width: { xs: "100%", md: "clamp(260px, 28%, 340px)" },
        maxWidth: "100%",
        minWidth: 0,
        minHeight: 0,
        alignSelf: "stretch",
        height: { md: "100%" },
        maxHeight: { md: "100%" },
        backgroundColor: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        ...ACCOUNT_DETAIL_FONT_SX,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#fff",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            p: { xs: 1.75, sm: 2 },
            borderBottom: "1px solid #e5e7eb",
            backgroundColor: "#f3f4f6",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 1,
            flexShrink: 0,
          }}
        >
          <Box sx={{ flex: 1, pr: 0.5, minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 700,
                color: "#111827",
                fontSize: "1rem",
                lineHeight: 1.4,
                mb: 0.75,
              }}
            >
              {guideInfo.title}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.8125rem",
                fontWeight: 400,
                lineHeight: 1.5,
                color: "#64748b",
              }}
            >
              {guideInfo.subtitle}
            </Typography>
          </Box>
          <Tooltip title="Hide guide" placement="left" arrow>
            <IconButton
              onClick={onClose}
              aria-label="Hide Single Sign-On setup guide"
              size="small"
              sx={{
                color: "#64748b",
                "&:hover": {
                  backgroundColor: "#e5e7eb",
                  color: "#111827",
                },
              }}
            >
              <ChevronRightIcon sx={{ fontSize: 22 }} />
            </IconButton>
          </Tooltip>
        </Box>

        <Box
          sx={{
            flex: 1,
            p: { xs: 1.5, sm: 2 },
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
            backgroundColor: "#fff",
          }}
        >
          <List sx={{ p: 0, flexShrink: 0 }}>
            {guideInfo.steps.map((step, index) => {
              const isSubStep = step.startsWith("  ");
              const stepText = isSubStep ? step.trim() : step;
              const isMainStep = isGuideMainStepText(step);
              if (isMainStep) mainStepCounter += 1;
              const stepNumber = isMainStep ? mainStepCounter : null;

              return (
                <React.Fragment key={`${index}-${stepText.slice(0, 24)}`}>
                  <ListItem
                    sx={{
                      px: 0,
                      py: 0.75,
                      alignItems: "flex-start",
                      pl: isSubStep ? 4 : 0,
                    }}
                  >
                    {isMainStep && stepNumber ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          backgroundColor: "rgba(249, 129, 37, 0.12)",
                          border: `1.5px solid ${BRAND_ORANGE}`,
                          mr: 1.75,
                          mt: 0.25,
                          flexShrink: 0,
                        }}
                      >
                        <Typography
                          sx={{
                            color: BRAND_ORANGE,
                            fontWeight: 700,
                            fontSize: "0.8125rem",
                            lineHeight: 1,
                          }}
                        >
                          {stepNumber}
                        </Typography>
                      </Box>
                    ) : null}
                    {isSubStep ? <Box sx={{ width: 28, mr: 1.75, flexShrink: 0 }} /> : null}
                    <ListItemText
                      primary={
                        <Typography
                          sx={{
                            color: "#334155",
                            fontSize: "0.8125rem",
                            fontWeight: 400,
                            lineHeight: 1.55,
                            whiteSpace: "pre-line",
                          }}
                        >
                          {stepText}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {index < guideInfo.steps.length - 1 &&
                    !guideInfo.steps[index + 1].startsWith("  ") &&
                    isGuideMainStepText(guideInfo.steps[index + 1]) && (
                      <Divider
                        sx={{
                          ml: 5.5,
                          mr: 1.5,
                          borderColor: "#e5e7eb",
                        }}
                      />
                    )}
                </React.Fragment>
              );
            })}
          </List>
        </Box>
      </Paper>
    </Box>
  );
}

function ConfirmTurnOffDialog({ open, onCancel, onConfirm }) {
  return (
    <ConfirmDialog
      open={open}
      title="Turn Off Single Sign-On"
      message="Are you sure you want to turn off Single Sign-On for this organization?"
      detail="Users set to Single Sign-On will no longer be offered Single Sign-On when they sign in. Anyone already signed in may stay signed in until they sign out or their session expires."
      confirmLabel="Turn Off"
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  );
}

function ConfirmDialog({
  open,
  title,
  message,
  detail,
  confirmLabel,
  confirmDisabled = false,
  confirmButtonSx,
  error,
  onCancel,
  onConfirm,
}) {
  return (
    <DialogShell open={open} title={title} onClose={onCancel}>
      <Typography sx={{ fontSize: "0.9375rem", color: "#334155", lineHeight: 1.5, mb: 1 }}>
        {message}
      </Typography>
      <Typography sx={HELP_TEXT_SX}>{detail}</Typography>
      {error && (
        <Alert severity="error" sx={{ ...ALERT_SX, mt: 2 }}>
          {error}
        </Alert>
      )}
      <DialogActionsRow>
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={confirmDisabled}
          sx={DIALOG_CANCEL_BTN_SX}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={onConfirm}
          disabled={confirmDisabled}
          sx={confirmButtonSx || DIALOG_CONFIRM_BTN_SX}
        >
          {confirmLabel}
        </Button>
      </DialogActionsRow>
    </DialogShell>
  );
}

function DialogActionsRow({ children }) {
  return (
    <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", flexWrap: "wrap", gap: 1.25 }}>
      {children}
    </Box>
  );
}

function DialogShell({ open, title, onClose, maxWidth = "xs", children }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      sx={{ zIndex: 1400 }}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          width: { xs: "calc(100% - 24px)", sm: "100%" },
          m: { xs: 1.5, sm: 2 },
          overflow: "hidden",
          ...ACCOUNT_DETAIL_FONT_SX,
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          backgroundColor: BRAND_ORANGE,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "1.15rem",
            color: "#FFFFFF",
            letterSpacing: "-0.02em",
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>
        <Box
          component="button"
          type="button"
          aria-label="Close"
          onClick={() => onClose()}
          sx={{
            p: 0,
            m: 0,
            border: "none",
            background: "transparent",
            color: "#FFFFFF",
            fontSize: 28,
            fontWeight: 700,
            lineHeight: 1,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <span aria-hidden="true">×</span>
        </Box>
      </Box>
      <DialogContent sx={{ px: 3, py: 2.5 }}>{children}</DialogContent>
    </Dialog>
  );
}

function ScimProvisioning({ canWrite }) {
  const [scim, setScim] = useState(null);
  const [loadingScim, setLoadingScim] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [pendingAction, setPendingAction] = useState("");
  const [actionError, setActionError] = useState("");
  const [confirmRotateOpen, setConfirmRotateOpen] = useState(false);
  const [confirmRevokeOpen, setConfirmRevokeOpen] = useState(false);
  // The one-time token lives only in this component's state, never in Redux or browser storage.
  const [issuedToken, setIssuedToken] = useState(null);
  const [baseUrlCopied, setBaseUrlCopied] = useState(false);

  const loadScimConfig = useCallback(async () => {
    setLoadingScim(true);
    setLoadError("");
    try {
      const response = await getScimConfig();
      if (response?.status === 200 && response.data && typeof response.data === "object") {
        setScim(response.data);
      } else {
        setLoadError(getApiErrorMessage(response));
      }
    } catch (error) {
      setLoadError(SCIM_LOAD_FALLBACK);
    } finally {
      setLoadingScim(false);
    }
  }, []);

  useEffect(() => {
    loadScimConfig();
  }, [loadScimConfig]);

  useEffect(() => {
    if (!baseUrlCopied) return undefined;
    const timer = setTimeout(() => setBaseUrlCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [baseUrlCopied]);

  const tokenConfigured = scim?.tokenConfigured === true;
  const baseUrl = typeof scim?.baseUrl === "string" ? scim.baseUrl : "";
  const busy = Boolean(pendingAction) || loadingScim;

  const copyBaseUrl = async () => {
    if (!baseUrl) return;
    try {
      await navigator.clipboard.writeText(baseUrl);
      setBaseUrlCopied(true);
    } catch (copyError) {
      setBaseUrlCopied(false);
    }
  };

  const issueToken = async (action) => {
    setConfirmRotateOpen(false);
    setPendingAction(action);
    setActionError("");
    try {
      const response = await postScimToken();
      if (response?.status !== 201) {
        setActionError(getApiErrorMessage(response));
        return;
      }
      const token = response.data?.token;
      const message = response.data?.message;
      if (typeof token === "string" && token) {
        setIssuedToken({
          value: token,
          message:
            (typeof message === "string" && message.trim()) || SCIM_TOKEN_FALLBACK_MESSAGE,
        });
      } else {
        setActionError(SCIM_ACTION_FALLBACK);
      }
      await loadScimConfig();
    } catch (error) {
      setActionError(SCIM_ACTION_FALLBACK);
    } finally {
      setPendingAction("");
    }
  };

  const revokeToken = async () => {
    setConfirmRevokeOpen(false);
    setPendingAction("revoke");
    setActionError("");
    try {
      const response = await deleteScimToken();
      const alreadyRevoked =
        response?.status === 404 && getApiErrorMessage(response) === SCIM_NOT_CONFIGURED;
      if (response?.status !== 204 && !alreadyRevoked) {
        setActionError(getApiErrorMessage(response));
        return;
      }
      if (response.status === 204) {
        enqueueSnackbar("SCIM token revoked.", { variant: "success" });
      }
      await loadScimConfig();
    } catch (error) {
      setActionError(SCIM_ACTION_FALLBACK);
    } finally {
      setPendingAction("");
    }
  };

  return (
    <>
      <SectionCard title="SCIM Provisioning">
        <Typography sx={HELP_TEXT_SX}>
          Add the SCIM Connector Base URL and token to the provisioning settings of the Okta app.
        </Typography>

        {loadingScim && <LinearProgress sx={PROGRESS_SX} />}

        {loadError && (
          <Alert
            severity="error"
            sx={ALERT_SX}
            action={
              <Button
                size="small"
                onClick={loadScimConfig}
                disabled={loadingScim}
                sx={{ textTransform: "none", fontWeight: 600, color: "inherit" }}
              >
                Retry
              </Button>
            }
          >
            {loadError}
          </Alert>
        )}

        {scim && (
          <>
            <CopyField
              label="SCIM Connector Base URL"
              value={baseUrl}
              copied={baseUrlCopied}
              onCopy={copyBaseUrl}
            />
            <Box sx={TWO_COL_SX}>
              <ReadOnlyValue label="Authorization" value={scim.authorizationScheme || ""} />
              <ReadOnlyValue
                label="Token status"
                value={tokenConfigured ? "Configured" : "Not configured"}
              />
              <ReadOnlyValue label="Created" value={formatDate(scim.createdAt)} />
              <ReadOnlyValue label="Last rotated" value={formatDate(scim.lastRotatedAt)} />
            </Box>
          </>
        )}

        {actionError && (
          <Alert severity="error" sx={ALERT_SX}>
            {actionError}
          </Alert>
        )}

        {canWrite && scim && (
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", flexWrap: "wrap", gap: 1.25 }}
          >
            {tokenConfigured ? (
              <>
                <Button
                  onClick={() => setConfirmRevokeOpen(true)}
                  disabled={busy}
                  sx={{ ...COPY_BTN_SX, minWidth: 112 }}
                >
                  {pendingAction === "revoke" ? "Revoking..." : "Revoke token"}
                </Button>
                <Button
                  onClick={() => setConfirmRotateOpen(true)}
                  disabled={busy}
                  sx={PRIMARY_BTN_SX}
                >
                  {pendingAction === "rotate" ? "Rotating..." : "Rotate token"}
                </Button>
              </>
            ) : (
              <Button onClick={() => issueToken("generate")} disabled={busy} sx={PRIMARY_BTN_SX}>
                {pendingAction === "generate" ? "Generating..." : "Generate token"}
              </Button>
            )}
          </Box>
        )}
      </SectionCard>

      <ConfirmDialog
        open={confirmRotateOpen}
        title="Rotate SCIM Token"
        message="Are you sure you want to rotate the SCIM token?"
        detail="The current token stops working immediately. Okta cannot provision users until the new token is added to the Okta app."
        confirmLabel="Rotate"
        onCancel={() => setConfirmRotateOpen(false)}
        onConfirm={() => issueToken("rotate")}
      />
      <ConfirmDialog
        open={confirmRevokeOpen}
        title="Revoke SCIM Token"
        message="Are you sure you want to revoke the SCIM token?"
        detail="The current token stops working immediately. Okta cannot provision users until a new token is generated and added to the Okta app."
        confirmLabel="Revoke"
        onCancel={() => setConfirmRevokeOpen(false)}
        onConfirm={revokeToken}
      />
      <ScimTokenDialog
        token={issuedToken?.value || ""}
        message={issuedToken?.message || ""}
        onClose={() => setIssuedToken(null)}
      />
    </>
  );
}

function ScimTokenDialog({ token, message, onClose }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const copyToken = async () => {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
    } catch (copyError) {
      setCopied(false);
    }
  };

  // Closing discards the token for good, so a stray backdrop click must not close the dialog.
  const handleClose = (event, reason) => {
    if (reason === "backdropClick") return;
    setCopied(false);
    onClose();
  };

  return (
    <DialogShell open={Boolean(token)} title="SCIM Token" onClose={handleClose} maxWidth="sm">
      <Typography sx={{ fontSize: "0.9375rem", color: "#334155", lineHeight: 1.5, mb: 2 }}>
        {message}
      </Typography>
      <CopyField label="Token" value={token} copied={copied} onCopy={copyToken} />
      <DialogActionsRow>
        <Button
          variant="contained"
          disableElevation
          onClick={handleClose}
          sx={DIALOG_CONFIRM_BTN_SX}
        >
          Done
        </Button>
      </DialogActionsRow>
    </DialogShell>
  );
}

function SectionCard({ title, action, children }) {
  return (
    <Box
      sx={{
        border: "1px solid #e2e8f0",
        borderRadius: "12px",
        backgroundColor: "#fff",
        minWidth: 0,
        width: "100%",
        maxWidth: "100%",
        flexShrink: 0,
        overflow: "visible",
        boxSizing: "border-box",
      }}
    >
      {(title || action) && (
        <Box
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: 1.5,
            borderBottom: "1px solid #f1f5f9",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            minWidth: 0,
          }}
        >
          <Typography sx={{ fontWeight: 600, color: "#111827", fontSize: "0.875rem" }}>
            {title}
          </Typography>
          {action}
        </Box>
      )}
      <Box
        sx={{
          p: { xs: 2, sm: 2.5 },
          display: "flex",
          flexDirection: "column",
          gap: 2,
          minWidth: 0,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function Field({ label, htmlFor, required = false, children }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography component="label" htmlFor={htmlFor} sx={FIELD_LABEL_SX}>
        {label}
        {required && (
          <Box component="span" aria-hidden="true" sx={REQUIRED_MARK_SX}>
            *
          </Box>
        )}
      </Typography>
      {children}
    </Box>
  );
}

function ReadOnlyValue({ label, value }) {
  const hasValue = Boolean(value);
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography component="div" sx={FIELD_LABEL_SX}>
        {label}
      </Typography>
      <Box sx={VALUE_BOX_SX}>
        <Typography
          component="div"
          sx={{
            width: "100%",
            fontSize: "0.8125rem",
            lineHeight: 1.5,
            color: hasValue ? "#64748b" : "#94a3b8",
            wordBreak: "break-all",
          }}
        >
          {hasValue ? value : "—"}
        </Typography>
      </Box>
    </Box>
  );
}

function TestConnectionStatus({ lastPassedOn, testSuccess = null, testMessage = "" }) {
  if (testMessage && testSuccess === false) {
    return (
      <Typography
        role="alert"
        sx={{
          ...HELP_TEXT_SX,
          color: "#dc2626",
        }}
      >
        {testMessage}
      </Typography>
    );
  }
  if (testMessage && testSuccess === true) {
    return (
      <Typography sx={{ ...HELP_TEXT_SX, color: "#047857" }}>
        {testMessage}
      </Typography>
    );
  }
  if (!lastPassedOn) return null;
  return (
    <Typography sx={HELP_TEXT_SX}>
      This URL passed Test Connection on {lastPassedOn}.
    </Typography>
  );
}

function CertificateField({ label, description, value, copied, onCopy, onDownload, hideLabel = false }) {
  const hasValue = Boolean(value);
  return (
    <Box sx={{ minWidth: 0 }}>
      {!hideLabel && (
        <Typography component="div" sx={FIELD_LABEL_SX}>
          {label}
        </Typography>
      )}
      <Typography sx={{ ...HELP_TEXT_SX, mb: 1 }}>{description}</Typography>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", minWidth: 0, flexWrap: "wrap" }}>
        <Box sx={{ ...VALUE_BOX_SX, flex: 1, minWidth: { xs: "100%", sm: 0 } }}>
          <Typography
            component="div"
            sx={{
              width: "100%",
              fontSize: "0.75rem",
              lineHeight: 1.5,
              fontFamily: "monospace",
              color: hasValue ? "#64748b" : "#94a3b8",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              userSelect: "all",
            }}
          >
            {hasValue ? value : "Available after you save the configuration."}
          </Typography>
        </Box>
        <Button
          onClick={onCopy}
          disabled={!hasValue}
          aria-label={copied ? `${label} copied` : `Copy ${label}`}
          sx={{
            ...COPY_BTN_SX,
            ...(copied
              ? {
                  color: BRAND_ORANGE,
                  borderColor: "#fdba74",
                  backgroundColor: "#fff7ed",
                  "&:hover": {
                    backgroundColor: "#fff7ed",
                    borderColor: "#fdba74",
                    boxShadow: "none",
                  },
                }
              : {}),
          }}
        >
          {copied ? "Copied" : "Copy"}
        </Button>
        <Button onClick={onDownload} disabled={!hasValue} sx={COPY_BTN_SX}>
          Download
        </Button>
      </Box>
    </Box>
  );
}

function CopyField({ label, value, copied, onCopy }) {
  return (
    <Box sx={{ minWidth: 0, width: "100%" }}>
      <Typography component="div" sx={FIELD_LABEL_SX}>
        {label}
      </Typography>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "center",
          minWidth: 0,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ ...VALUE_BOX_SX, flex: "1 1 160px", minWidth: 0, overflow: "hidden" }}>
          <Typography
            component="div"
            title={value || undefined}
            sx={{
              width: "100%",
              fontSize: "0.8125rem",
              lineHeight: 1.5,
              color: value ? "#64748b" : "#94a3b8",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              userSelect: "all",
            }}
          >
            {value || "—"}
          </Typography>
        </Box>
        <Button
          onClick={onCopy}
          disabled={!value}
          aria-label={copied ? `${label} copied` : `Copy ${label}`}
          sx={{
            ...COPY_BTN_SX,
            ...(copied
              ? {
                  color: BRAND_ORANGE,
                  borderColor: "#fdba74",
                  backgroundColor: "#fff7ed",
                  "&:hover": {
                    backgroundColor: "#fff7ed",
                    borderColor: "#fdba74",
                    boxShadow: "none",
                  },
                }
              : {}),
          }}
        >
          {copied ? "Copied" : "Copy"}
        </Button>
      </Box>
    </Box>
  );
}
